import { useState, useEffect, useRef } from 'react';

export default function HowItWorks() {
  const [timelineProgress, setTimelineProgress] = useState(0);
  const timelineRef = useRef<HTMLDivElement>(null);

  // Timeline scroll animation - Exact logic from your original code
  useEffect(() => {
    const handleScroll = () => {
      if (!timelineRef.current) return;

      const rect = timelineRef.current.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      const timelineTop = rect.top;
      const timelineHeight = rect.height;

      const startOffset = windowHeight * 0.8;
      const endOffset = windowHeight * 0.2;

      if (timelineTop < startOffset && timelineTop + timelineHeight > endOffset) {
        const visibleTop = Math.max(0, startOffset - timelineTop);
        const visibleHeight = Math.min(timelineHeight, startOffset - endOffset);
        const scrollProgress = Math.min(1, visibleTop / visibleHeight);
        setTimelineProgress(scrollProgress);
      } else if (timelineTop >= startOffset) {
        setTimelineProgress(0);
      } else if (timelineTop + timelineHeight <= endOffset) {
        setTimelineProgress(1);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <section className="relative py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Your Healthcare Journey</h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Follow our comprehensive timeline to transform your healthcare practice and connect with more patients
          </p>
        </div>
        
        <div className="relative" ref={timelineRef}>
          {/* Animated Timeline Line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gray-200 rounded-full overflow-hidden">
            <div 
              className="w-full bg-gradient-to-b from-teal-400 via-teal-500 to-teal-600 rounded-full transition-all duration-300 ease-out"
              style={{ 
                height: `${timelineProgress * 100}%`,
                transformOrigin: 'top'
              }}
            ></div>
          </div>

          {/* STEP 1: Claim Your Free Page - LEFT */}
          <div className="relative mb-24">
            {/* Timeline Icon */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-teal-500 shadow-lg z-10 flex items-center justify-center">
              <i className="ri-hospital-line text-2xl text-teal-600"></i>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:pr-12">
                <div className="rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-cyan-50 to-teal-50 border border-gray-100 shadow-xl">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 bg-teal-500 shadow-lg shadow-teal-500/30">
                      <i className="ri-hospital-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">STEP 1</div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Claim your free page</h2>
                      <div className="h-1 w-20 rounded-full bg-teal-500"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">
                    Establish trust and help patients get to know your practice
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    An average of 500,000 people visit Revulist each day to find healthcare providers, specialists, and medical facilities. Get found by these motivated patients, connect with them, and manage your page all for free.
                  </p>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Update your practice info so patients can find you</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Respond to reviews and messages as soon as they come in</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Add photos to showcase the best of your practice</span>
                    </li>
                  </ul>
                  <a
                    href="/claim"
                    className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    Verify my free listing
                  </a>
                </div>
              </div>
              <div className="lg:pl-12">
                <div className="relative group">
                  <img
                    alt="Claim your listing"
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                    src="https://readdy.ai/api/search-image?query=Healthcare%20provider%20managing%20their%20online%20profile%20on%20tablet%20device%2C%20modern%20medical%20office%20setting%20with%20doctor%20updating%20practice%20information%2C%20professional%20medical%20technology%20interface%20with%20patient%20reviews%20and%20appointment%20scheduling&width=600&height=500&seq=claim-listing&orientation=landscape"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* STEP 2: MedReview Ads - RIGHT */}
          <div className="relative mb-24">
            {/* Timeline Icon */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-teal-500 shadow-lg z-10 flex items-center justify-center">
              <i className="ri-megaphone-line text-2xl text-teal-600"></i>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:pr-12 order-2 lg:order-1">
                <div className="relative group">
                  <img
                    alt="MedReview Ads"
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                    src="https://readdy.ai/api/search-image?query=Medical%20practice%20advertising%20dashboard%20on%20computer%20screen%20showing%20patient%20lead%20generation%2C%20healthcare%20marketing%20analytics%20with%20graphs%20and%20patient%20appointment%20bookings%2C%20modern%20medical%20office%20technology&width=600&height=400&seq=medreview-ads&orientation=landscape"
                  />
                </div>
              </div>
              <div className="lg:pl-12 order-1 lg:order-2">
                <div className="rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-teal-50 to-cyan-50 border border-gray-100 shadow-xl">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 bg-teal-500 shadow-lg shadow-teal-500/30">
                      <i className="ri-megaphone-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">STEP 2</div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Revulist Ads</h2>
                      <div className="h-1 w-20 rounded-full bg-teal-500"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">
                    Practices get 4x more patient leads with Revulist Ads
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Reach more patients who are searching for healthcare providers like you. Our targeted advertising platform helps you connect with patients actively looking for your services.
                  </p>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Prominent display on search and competitors' pages</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Target patients by location and specialty</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Cancel anytime with flexible pricing</span>
                    </li>
                  </ul>
                  <a
                    href="/ads"
                    className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    Learn more about ads
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* STEP 3: Patient Connect - LEFT */}
          <div className="relative mb-24">
            {/* Timeline Icon */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-teal-500 shadow-lg z-10 flex items-center justify-center">
              <i className="ri-user-heart-line text-2xl text-teal-600"></i>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:pr-12">
                <div className="rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-blue-50 to-teal-50 border border-gray-100 shadow-xl">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 bg-teal-500 shadow-lg shadow-teal-500/30">
                      <i className="ri-user-heart-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">STEP 3</div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Patient Connect</h2>
                      <div className="h-1 w-20 rounded-full bg-teal-500"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">
                    Build stronger relationships with your patients
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Engage with your patients through our comprehensive communication platform. Send appointment reminders, follow-up messages, and health tips to keep your patients informed and engaged.
                  </p>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Automated appointment reminders and confirmations</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Secure messaging platform for patient communication</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Health education content sharing</span>
                    </li>
                  </ul>
                  <a
                    href="/patient-connect"
                    className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    Start connecting
                  </a>
                </div>
              </div>
              <div className="lg:pl-12">
                <div className="relative group">
                  <img
                    alt="Patient Connect"
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                    src="https://readdy.ai/api/search-image?query=Healthcare%20provider%20communicating%20with%20patient%20through%20digital%20platform%2C%20modern%20telemedicine%20interface%20showing%20appointment%20reminders%20and%20health%20messages%2C%20professional%20medical%20communication%20technology&width=600&height=500&seq=patient-connect&orientation=landscape"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* STEP 4: Analytics & Insights - RIGHT */}
          <div className="relative mb-24">
            {/* Timeline Icon */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-teal-500 shadow-lg z-10 flex items-center justify-center">
              <i className="ri-bar-chart-line text-2xl text-teal-600"></i>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:pr-12 order-2 lg:order-1">
                <div className="relative group">
                  <img
                    alt="Analytics dashboard"
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                    src="https://readdy.ai/api/search-image?query=Healthcare%20analytics%20dashboard%20on%20computer%20screen%20showing%20patient%20metrics%20and%20performance%20data%2C%20medical%20practice%20management%20software%20with%20graphs%20and%20statistics%2C%20modern%20healthcare%20technology&width=600&height=500&seq=analytics-insights&orientation=landscape"
                  />
                </div>
              </div>
              <div className="lg:pl-12 order-1 lg:order-2">
                <div className="rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-indigo-50 to-blue-50 border border-gray-100 shadow-xl">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 bg-teal-500 shadow-lg shadow-teal-500/30">
                      <i className="ri-bar-chart-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">STEP 4</div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Analytics & Insights</h2>
                      <div className="h-1 w-20 rounded-full bg-teal-500"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">
                    Make data-driven decisions for your practice
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Get detailed insights into your practice performance, patient demographics, and marketing effectiveness. Use our advanced analytics to optimize your patient acquisition and retention strategies.
                  </p>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Comprehensive practice performance dashboards</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Patient acquisition and retention metrics</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Marketing ROI tracking and optimization</span>
                    </li>
                  </ul>
                  <a
                    href="/analytics"
                    className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    View analytics
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* STEP 5: Healthcare Networks - LEFT */}
          <div className="relative mb-0">
            {/* Timeline Icon */}
            <div className="absolute left-1/2 transform -translate-x-1/2 w-16 h-16 bg-white rounded-full border-4 border-teal-500 shadow-lg z-10 flex items-center justify-center">
              <i className="ri-building-line text-2xl text-teal-600"></i>
            </div>
            
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="lg:pr-12">
                <div className="rounded-3xl p-8 lg:p-12 bg-gradient-to-br from-purple-50 to-indigo-50 border border-gray-100 shadow-xl">
                  <div className="flex items-center mb-8">
                    <div className="w-16 h-16 rounded-full flex items-center justify-center mr-6 bg-teal-500 shadow-lg shadow-teal-500/30">
                      <i className="ri-building-line text-2xl text-white"></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-teal-600 mb-1">STEP 5</div>
                      <h2 className="text-3xl font-bold text-gray-900 mb-2">Healthcare Networks</h2>
                      <div className="h-1 w-20 rounded-full bg-teal-500"></div>
                    </div>
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-6 leading-tight">
                    Expand your reach through our provider network
                  </h3>
                  <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                    Join our extensive network of healthcare providers and facilities. Collaborate with other specialists, receive referrals, and grow your practice through our integrated healthcare ecosystem.
                  </p>
                  <ul className="space-y-4 mb-10">
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Direct referrals from other network providers</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Collaborative care opportunities</span>
                    </li>
                    <li className="flex items-start">
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center mr-4 mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-white text-sm"></i>
                      </div>
                      <span className="text-gray-700 text-lg">Access to exclusive network resources</span>
                    </li>
                  </ul>
                  <a
                    href="/networks"
                    className="inline-flex items-center justify-center font-medium rounded-lg transition-all duration-200 whitespace-nowrap cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 bg-teal-600 hover:bg-teal-700 text-white shadow-sm hover:shadow-md focus:ring-teal-500 px-6 py-3 text-base whitespace-nowrap shadow-lg hover:shadow-xl"
                  >
                    Join the network
                  </a>
                </div>
              </div>
              <div className="lg:pl-12">
                <div className="relative group">
                  <img
                    alt="Healthcare Networks"
                    className="w-full h-auto rounded-2xl shadow-2xl transition-transform duration-500 group-hover:scale-105"
                    src="https://readdy.ai/api/search-image?query=Medical%20network%20visualization%20showing%20connected%20healthcare%20providers%20and%20facilities%2C%20digital%20map%20of%20health%20system%20connections%20with%20professional%20medical%20icons%2C%20modern%20healthcare%20ecosystem%20illustration&width=600&height=500&seq=healthcare-networks&orientation=landscape"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}